"use client"

import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { AlertTriangle, Wifi, WifiOff } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useMyCIOLanguageOrCookie } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOShellChromeCopy } from '@/lib/mycio/copy/shellChrome'

// Rendered by the root layout, outside the language provider: the language comes from the cookie.

export function NetworkStatusIndicator() {
  const { isOnline, hasInternetAccess, isChecking } = useNetworkStatus()
  const t = getMyCIOShellChromeCopy(useMyCIOLanguageOrCookie()).network
  const [showOfflineIndicator, setShowOfflineIndicator] = useState(false)
  const [offlineStartTime, setOfflineStartTime] = useState<number | null>(null)

  // Track offline state with persistent checking over 5 seconds
  useEffect(() => {
    const isOffline = !isOnline || !hasInternetAccess
    
    if (isOffline && !offlineStartTime) {
      // Start tracking offline time
      setOfflineStartTime(Date.now())
    } else if (!isOffline) {
      // Reset when back online
      setOfflineStartTime(null)
      setShowOfflineIndicator(false)
    }

    if (isOffline && offlineStartTime) {
      // Check if we've been offline for 5 seconds
      const timeOffline = Date.now() - offlineStartTime
      if (timeOffline >= 5000) {
        setShowOfflineIndicator(true)
      }
    }
  }, [isOnline, hasInternetAccess, offlineStartTime])

  // Don't show anything if everything is working or we haven't been offline long enough
  if ((isOnline && hasInternetAccess) || !showOfflineIndicator) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-800 bg-opacity-90 text-white px-3 py-2 rounded-lg text-xs font-medium shadow-lg border border-gray-600">
      <div className="flex items-center gap-2">
        {!isOnline ? (
          <>
            <WifiOff className="h-3 w-3" />
            <span>{t.offline}</span>
          </>
        ) : !hasInternetAccess ? (
          <>
            <AlertTriangle className="h-3 w-3" />
            <span>{t.noInternet}</span>
          </>
        ) : null}
      </div>
    </div>
  )
}

export function NetworkGate({ children }: { children: React.ReactNode }) {
  const { isOnline, hasInternetAccess, isChecking } = useNetworkStatus()
  const t = getMyCIOShellChromeCopy(useMyCIOLanguageOrCookie()).network
  const [showOfflineScreen, setShowOfflineScreen] = useState(false)
  const [offlineStartTime, setOfflineStartTime] = useState<number | null>(null)

  // Track offline state with persistent checking over 5 seconds
  useEffect(() => {
    const isOffline = !isOnline || (!hasInternetAccess && !isChecking)
    
    if (isOffline && !offlineStartTime) {
      // Start tracking offline time
      setOfflineStartTime(Date.now())
    } else if (!isOffline) {
      // Reset when back online
      setOfflineStartTime(null)
      setShowOfflineScreen(false)
    }

    if (isOffline && offlineStartTime) {
      // Check if we've been offline for 5 seconds
      const timeOffline = Date.now() - offlineStartTime
      if (timeOffline >= 5000) {
        setShowOfflineScreen(true)
      }
    }
  }, [isOnline, hasInternetAccess, isChecking, offlineStartTime])

  // Only show loading state briefly while checking on initial load
  if (isChecking && !hasInternetAccess && !offlineStartTime) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-primary">
        <div className="text-center">
          <Wifi className="h-8 w-8 animate-pulse mx-auto mb-4 text-[var(--supreme-primary)]" />
          <p className="text-dark-text-secondary">{t.checking}</p>
        </div>
      </div>
    )
  }

  // Only block app if clearly offline for more than 5 seconds
  if (showOfflineScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-primary">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <WifiOff className="h-16 w-16 mx-auto text-[var(--supreme-error)]" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-4">
            {t.noConnectionTitle}
          </h1>
          
          <p className="text-dark-text-secondary mb-6">
            {t.noConnectionBody}
          </p>
          
          <div className="bg-blue-900/30 border border-blue-600/30 rounded-lg p-4">
            <p className="text-sm text-[var(--supreme-primary)]">
              <strong>MyCIO</strong>{t.resumesAfterBrand}
            </p>
          </div>
          
          <div className="mt-6 text-xs text-dark-text-secondary">
            {t.waiting}
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}